import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { createSupplier, getSupplierAccountingOptions } from '../../../services/SuppliersService';
import SupplierAccountingFields, { accountingPayload, emptyAccounting } from './SupplierAccountingFields';

// The inline "Add supplier" step of the expense form: same fields as the Suppliers page. The supplier is created in
// Management (single source of truth); creating its Rompslomp contact is a separate, deliberate step in Finance.
export default function SupplierQuickAdd({ show, onHide, onCreated, initialName = '' }) {
    const { t } = useTranslation();
    const [options, setOptions] = useState({ cost_accounts: [], treatments: [] });

    useEffect(() => {
        if (!show) return;
        (async () => {
            try { setOptions((await getSupplierAccountingOptions()).data); } catch (_) { /* categories stay optional */ }
        })();
    }, [show]);

    const schema = Yup.object({
        name: Yup.string().trim().required(t('suppliers.errors.nameRequired')),
        email: Yup.string().trim().email(t('suppliers.accounting.errors.email')),
        country: Yup.string().trim().test('iso', t('suppliers.accounting.errors.country'), (v) => !v || /^[A-Za-z]{2}$/.test(v)),
    });

    const submit = async (values, { setSubmitting }) => {
        try {
            const res = await createSupplier({
                name: values.name.trim(), phone: values.phone.trim() || null, email: values.email.trim() || null, ...accountingPayload(values),
            });
            toast.success(t('suppliers.added'));
            onCreated(res.data);
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || t('suppliers.errors.save'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Formik enableReinitialize initialValues={{ name: initialName, phone: '', email: '', ...emptyAccounting() }} validationSchema={schema} onSubmit={submit}>
                {({ handleSubmit, handleChange, setFieldValue, values, errors, touched, isSubmitting }) => (
                    <Form noValidate onSubmit={handleSubmit} data-testid="supplier-quick-add">
                        <Modal.Header closeButton><Modal.Title>{t('suppliers.quickAdd.title')}</Modal.Title></Modal.Header>
                        <Modal.Body>
                            <p className="text-muted small">{t('suppliers.quickAdd.help')}</p>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-2" controlId="quick-supplier-name">
                                        <Form.Label>{t('suppliers.modal.nameLabel')}</Form.Label>
                                        <Form.Control name="name" value={values.name} onChange={handleChange} isInvalid={touched.name && !!errors.name} />
                                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-2" controlId="quick-supplier-phone">
                                        <Form.Label>{t('suppliers.modal.phoneLabel')}</Form.Label>
                                        <Form.Control name="phone" value={values.phone} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-2" controlId="quick-supplier-email">
                                        <Form.Label>{t('suppliers.modal.emailLabel')}</Form.Label>
                                        <Form.Control name="email" value={values.email} onChange={handleChange} isInvalid={touched.email && !!errors.email} />
                                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <SupplierAccountingFields values={values} onChange={setFieldValue} options={options} errors={values.country && errors.country ? { country: errors.country } : {}} idPrefix="quick-supplier" />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="light" onClick={onHide}>{t('common.cancel')}</Button>
                            <Button type="submit" disabled={isSubmitting} data-testid="quick-add-save">{t('suppliers.quickAdd.save')}</Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}
